/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponent, IComponentProps, msdyn365Commerce } from '@msdyn365-commerce/core';
import { getPayloadObject, getTelemetryAttributes, IPayLoad, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import React from 'react';

import classnames from 'classnames';

/**
 * Class names used in component.
 */
const componentClassName = 'msc-refiner-item-check';
const labelClassName = `${componentClassName}__label`;

/**
 * IRefinerItemCheckComponent interface.
 */
interface IRefinerItemCheckComponent extends IComponent<IRefinerItemCheckProps> {}

/**
 * IRefinerItemCheckProps interface.
 */
export interface IRefinerItemCheckProps extends IComponentProps<{}> {
    className?: string;
    label: string;
    ariaLabel?: string;
    isDisabled?: boolean;
    isSingleSelect: boolean;
    isChecked?: boolean;
    telemetryContent?: ITelemetryContent;
    onClick(): void;
}

/**
 * RefinerItemCheck component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const RefinerItemCheckFunctionalComponent: React.FC<IRefinerItemCheckProps> = (props: IRefinerItemCheckProps) => {
    const { className, isDisabled, label, ariaLabel, isSingleSelect, isChecked, onClick, telemetryContent } = props;

    let itemTypeClassName = isSingleSelect ? 'single-select' : 'multi-select';
    itemTypeClassName = isChecked ? `${itemTypeClassName}-checked` : itemTypeClassName;

    // define telemetry attributes
    const payLoad: IPayLoad = getPayloadObject('click', telemetryContent!, '');
    payLoad.contentAction.etext = label;
    const refinerItemAttributes = getTelemetryAttributes(telemetryContent!, payLoad);

    const onClickHandler = (event: React.MouseEvent<HTMLAnchorElement | HTMLInputElement>): void => {
        event.preventDefault();
        event.stopPropagation();

        !props.isDisabled && onClick();
    };

    return (
        <a
            tabIndex={0}
            className={classnames(className, componentClassName, itemTypeClassName, isDisabled ? 'disabled' : '')}
            role={isSingleSelect ? 'radio' : 'checkbox'}
            aria-label={ariaLabel ?? label}
            aria-checked={isChecked}
            aria-disabled={isDisabled}
            onClick={onClickHandler}
            {...refinerItemAttributes}
        >
            <span className={labelClassName} aria-hidden='true'>
                {label}
            </span>
        </a>
    );
};

/**
 * RefinerItemCheck component.
 */
export const RefinerItemCheckComponent: React.FunctionComponent<IRefinerItemCheckProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error -- Required for this signature.
    IRefinerItemCheckComponent
>('Refiner-Item-Check', {
    component: React.memo(RefinerItemCheckFunctionalComponent)
});
